/**
 * Shared domain types — platform-agnostic (no DOM, no React Native).
 * Consumed by apps/web now and apps/mobile (Expo) later.
 */

export type IdeaStatus =
  | 'draft'
  | 'in_research'
  | 'awaiting_refinement'
  | 'report_ready';

export type ReportKind = 'deck' | 'detailed' | 'verdict';

export interface User {
  id: string;
  name: string | null;
  email: string;
  emailVerified: boolean;
  image: string | null;
  createdAt: string;
}

export interface Report {
  id: string;
  ideaId: string;
  kind: ReportKind;
  /** True once the deliverable exists and can be fetched via the authed endpoint. */
  available: boolean;
  createdAt: string;
}

export interface Idea {
  id: string;
  title: string;
  summary: string | null;
  status: IdeaStatus;
  /** Null until the idea has been scanned. */
  score: number | null;
  /** Display string, e.g. "88TH PERCENTILE" — higher is better. Null until scanned. */
  percentile: string | null;
  isNew: boolean;
  /**
   * The delivered report's call ('proceed' | …). Null until a report lands.
   * Drives the verdict chip beside the status badge on the dashboard card.
   */
  verdict: string | null;
  /**
   * Refinement progress, for the card's meta line ("2 OF 4 QUESTIONS
   * ANSWERED"). Both zero before the scan generates any queries.
   */
  refinement: { answered: number; total: number };
  createdAt: string;
  updatedAt: string;
  reports: Report[];
}

/**
 * Dashboard grouping. Mirrors the "JUST LANDED / WAITING ON YOU / IN MOTION"
 * sections in the design.
 */
export type IdeaGroupKey = 'just_landed' | 'waiting_on_you' | 'in_motion';

export interface IdeaGroup {
  key: IdeaGroupKey;
  label: string;
  ideas: Idea[];
}

export interface IdeasResponse {
  groups: IdeaGroup[];
  /** Triage line above the dashboard heading. */
  triage: string;
  total: number;
}

export interface MeResponse {
  user: User;
}

/** Runtime flags the SPA needs before it can render a gated surface. */
export interface FeaturesResponse {
  feedbackEnabled: boolean;
}

/** Server response after saving an annotation submission. */
export interface FeedbackResponse {
  id: string;
  notes: number;
}

/** Discriminated API error shape returned by the Worker. */
export interface ApiErrorBody {
  error: {
    code: ApiErrorCode;
    message: string;
  };
}

export type ApiErrorCode =
  | 'unauthorized'
  | 'forbidden'
  | 'email_not_verified'
  | 'not_found'
  | 'rate_limited'
  | 'invalid_request'
  | 'captcha_failed'
  | 'internal';

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;

  constructor(code: ApiErrorCode, message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

/** Maps an idea status to its dashboard group. */
export function groupForStatus(status: IdeaStatus): IdeaGroupKey {
  switch (status) {
    case 'report_ready':
      return 'just_landed';
    case 'awaiting_refinement':
    case 'draft':
      return 'waiting_on_you';
    case 'in_research':
      return 'in_motion';
  }
}

export const GROUP_LABELS: Record<IdeaGroupKey, string> = {
  just_landed: 'JUST LANDED',
  waiting_on_you: 'WAITING ON YOU',
  in_motion: 'IN MOTION',
};
